import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date with Thai Buddhist calendar (date only)
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "15 มกราคม 2568")
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return 'ไม่ระบุ';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'ไม่ระบุ';

    const thaiYear = date.getFullYear() + 543;
    return format(date, 'dd MMMM ', { locale: th }) + thaiYear;
  } catch {
    return 'ไม่ระบุ';
  }
}

/**
 * Format date and time with Thai Buddhist calendar
 * @param dateString - ISO date string
 * @returns Formatted date and time string (e.g., "15 มกราคม 2568 เวลา 14:30")
 */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'ไม่ระบุ';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'ไม่ระบุ';

    const thaiYear = date.getFullYear() + 543;
    const dateFormatted = format(date, 'dd MMMM ', { locale: th }) + thaiYear;
    const timeFormatted = format(date, 'HH:mm');

    return `${dateFormatted} เวลา ${timeFormatted}`;
  } catch {
    return 'ไม่ระบุ';
  }
}

/**
 * Format timestamp to separate date and time objects (for flexible display)
 * @param dateString - ISO date string
 * @returns Object with date and time strings
 */
export function formatTimestamp(dateString: string | null): { date: string; time: string } {
  if (!dateString) {
    return { date: 'ไม่ระบุ', time: '' };
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return { date: 'ไม่ระบุ', time: '' };
    }

    return {
      date: date.toLocaleDateString('th-TH'),
      time: date.toLocaleTimeString('th-TH', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  } catch {
    return { date: 'ไม่ระบุ', time: '' };
  }
}
